import React, { useState, useEffect } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Paper, Chip, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Typography, Button } from '@mui/material';
import styles from './TaskList.module.css';
import { Task, TaskStatusDTO } from '../../../../types/task';
import { TaskStatus } from '../../../../types/task-status'; // Добавлено
// import { Category } from '../../../../types/category';
import { taskService } from '../../../../services/taskService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext'; // Импорт useAuth
import { TaskUserStatus } from '../../../../types/TaskUserStatus';

interface TaskListProps {
    tasks: Task[];
    onStatusChange: (status: TaskStatus | undefined, newestFirst: boolean) => void;
    onTaskUpdate: (updatedTask: Task) => void; // Добавляем новую функцию для обновления одной задачи
    selectedStatus?: TaskStatus;
    newestFirst: boolean;
}

const TaskList: React.FC<TaskListProps> = ({ tasks, onStatusChange, onTaskUpdate, selectedStatus, newestFirst }) => {
    const [taskStatuses, setTaskStatuses] = useState<TaskStatusDTO[]>([]);
    const navigate = useNavigate();
    const { state: { user: authenticatedUser } } = useAuth(); // Получаем данные пользователя из контекста

    const handleSortChange = (event: SelectChangeEvent<string>) => {
        onStatusChange(selectedStatus, event.target.value === 'true');
    };

    // const getCurrentUserTaskUserStatus = (task: Task): TaskUserStatus | undefined => {
    //     if (!authenticatedUser?.user?.id || !task.taskUsers) return undefined;
    //     const currentUserTaskUser = task.taskUsers.find(tu => tu.userId === authenticatedUser.user?.id);
    //     return currentUserTaskUser?.status;
    // };

    const handleAcceptTask = async (taskUserId: string) => {
        try {
            const updatedTaskUser = await taskService.acceptTask(taskUserId);
            // Находим задачу, соответствующую taskUserId
            const taskToUpdate = tasks.find(task => task.taskUsers?.some(tu => tu.id === taskUserId));
            if (taskToUpdate) {
                // Обновляем статус TaskUser локально
                const updatedTask = { ...taskToUpdate };
                updatedTask.taskUsers = updatedTask.taskUsers?.map(tu => 
                    tu.id === taskUserId ? { ...tu, status: updatedTaskUser.status } : tu
                );
                // Также обновляем общий статус задачи (TaskEntity) на InProgress
                updatedTask.status = TaskStatus.InProgress;
                updatedTask.currentExecutorId = authenticatedUser?.user?.id;
                updatedTask.assignedAt = new Date().toISOString(); // Обновляем assignedAt
                onTaskUpdate(updatedTask); // Передаем обновленную задачу родительскому компоненту
            }
            console.log("Задача успешно принята!");
        } catch (error) {
            console.error("Ошибка при принятии задачи:", error);
        }
    };

    const handleRejectTask = async (taskUserId: string) => {
        try {
            const updatedTaskUser = await taskService.rejectTask(taskUserId);
            // Находим задачу, соответствующую taskUserId
            const taskToUpdate = tasks.find(task => task.taskUsers?.some(tu => tu.id === taskUserId));
            if (taskToUpdate) {
                // Обновляем статус TaskUser локально
                const updatedTask = { ...taskToUpdate };
                updatedTask.taskUsers = updatedTask.taskUsers?.map(tu => 
                    tu.id === taskUserId ? { ...tu, status: updatedTaskUser.status } : tu
                );
                onTaskUpdate(updatedTask); // Передаем обновленную задачу родительскому компоненту
            }
            console.log("Задача успешно отклонена.");
        } catch (error) {
            console.error("Ошибка при отклонении задачи:", error);
        }
    };

    useEffect(() => {
        const fetchTaskStatuses = async () => {
            try {
                const response = await taskService.getTaskStatuses();
                setTaskStatuses(response);
            } catch (err) {
                console.error('Failed to fetch task statuses.', err);
            } finally {
            }
        };
        fetchTaskStatuses();
    }, []);

    const getStatusChipColor = (status: TaskStatus) => {
        switch (status) {
            case TaskStatus.Paid:
            case TaskStatus.Completed:
            case TaskStatus.Verified:
                return "success";
            case TaskStatus.Review:
                return "warning";
            case TaskStatus.Rejected:
            case TaskStatus.Cancelled:
                return "error";
            case TaskStatus.InProgress:
            case TaskStatus.Assigned:
            case TaskStatus.Selected:
            case TaskStatus.Confirmed:
                return "info";
            default:
                return "default";
        }
    };

    // Логика для определения, нужно ли показывать уведомление о новой задаче
    const assignedTask = tasks.find(task => 
        task.taskUsers?.some(tu => tu.userId === authenticatedUser?.user?.id && tu.status === TaskUserStatus.Assigned)
    );

    const isUserWorking = tasks.some(task => 
        task.taskUsers?.some(tu => tu.userId === authenticatedUser?.user?.id && tu.status === TaskUserStatus.Working)
    );

    const showNotification = assignedTask && !isUserWorking;

    return (
        <Paper className={styles.container}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 200, mr: 2 }}>
                    <InputLabel>Статус задания</InputLabel>
                    <Select
                        value={selectedStatus || ''}
                        onChange={(e) => onStatusChange(e.target.value as TaskStatus || undefined, newestFirst)}
                        label="Статус задания"
                    >
                        <MenuItem value="">Все</MenuItem>
                        {taskStatuses.map((ts) => (
                            <MenuItem key={ts.statusCode} value={ts.statusCode as TaskStatus}>
                                {ts.statusName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Сортировка</InputLabel>
                    <Select
                        value={newestFirst.toString()}
                        onChange={handleSortChange}
                        label="Сортировка"
                    >
                        <MenuItem value="true">Сначала новые</MenuItem>
                        <MenuItem value="false">Сначала старые</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Table className={styles.table}>
                <TableHead>
                    <TableRow className={styles.tableHeader}>
                        <TableCell>Название задания</TableCell>
                        <TableCell>Категория</TableCell>
                        <TableCell>Дедлайн</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell>Стоимость</TableCell>
                        <TableCell>Заказчик</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {tasks.map((task) => {
                        const currentUserTaskUser = task.taskUsers?.find(tu => tu.userId === authenticatedUser?.user?.id);
                        const currentUserTaskUserStatus = currentUserTaskUser?.status;

                        return (
                            <TableRow
                                key={task.id}
                                className={styles.tableRow}
                                onClick={() => {
                                    console.log('Navigating to task:', task.id);
                                    navigate(`/executor/task/${task.id}`);
                                }}
                                sx={{ cursor: 'pointer' }}
                            >
                                <TableCell>{task.title}</TableCell>
                                <TableCell>{task.category?.title || '—'}</TableCell>
                                <TableCell style={{ color: task.deadline && new Date(task.deadline) < new Date() ? 'red' : 'inherit' }}>
                                    {task.deadline ? new Date(task.deadline).toLocaleDateString() : '—'}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={currentUserTaskUserStatus || task.status}
                                        color={getStatusChipColor(task.status)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>{task.priceInKopecks ? `${task.priceInKopecks / 100}₽` : '0₽'}</TableCell>
                                <TableCell>{task.organization?.name || '—'}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
            {showNotification && assignedTask && assignedTask.taskUsers?.find(tu => tu.userId === authenticatedUser?.user?.id) && (
                <Box className={styles.notificationRow} sx={{ mt: 3, p: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Box className={styles.notificationBox}>
                        <Box>
                            <Typography variant="h6" component="div" className={styles.notificationTitle}>
                                Прочитал ТЗ полностью и готов принять задачу?
                            </Typography>
                            <Typography variant="body2" className={styles.notificationText}>
                                Появилась новая задача! Решай сам: хочешь взять её и попробовать свои силы прямо сейчас, или пропускаешь и ждёшь следующую возможность, которая может быть даже интереснее?
                            </Typography>
                        </Box>
                        <Box className={styles.notificationActions}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={(e) => { e.stopPropagation(); handleRejectTask(assignedTask.taskUsers!.find(tu => tu.userId === authenticatedUser?.user?.id)!.id); }}
                            >
                                Отклонить
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={(e) => { e.stopPropagation(); handleAcceptTask(assignedTask.taskUsers!.find(tu => tu.userId === authenticatedUser?.user?.id)!.id); }}
                            >
                                Принять задачу
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}
        </Paper>
    );
};

export default TaskList;
